import type { EntityFormErrorMap } from '@klear/ui/entity-form-state'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { Draft } from 'immer'
import { Check, Plus } from 'lucide-react'
import isString from 'lodash/isString'
import values from 'lodash/values'
import toPairs from 'lodash/toPairs'
import type { AddressJson } from '@klear/address'
import { PAYMENT_NETWORK_ACH, PAYMENT_NETWORK_WIRE } from '@klear/bank-account/constants'
import { Alert, AlertDescription, AlertTitle } from '@klear/ui/components/alert'
import { Card, CardContent, CardHeader, CardTitle } from '@klear/ui/components/card'
import { useEntityWizardForm } from '@klear/ui/hooks/use-entity-wizard-form'
import type { WizardStepGroupNavState } from '@klear/ui/wizard/nav-state'
import { bindWizardSteps, type WizardStepModule } from '@klear/ui/wizard/flow'
import { WizardFlowContent } from '@klear/ui/wizard/flow-content'
import { useReportedWizardFlow } from '@klear/ui/wizard/use-step-group-nav'
import {
  initialExternalBankAccountForm,
  externalBankAccountReadToForm,
  seedExternalBankAccountFormAddress,
  type ExternalBankAccountForm,
  type ExternalBankAccountFormDirty,
  type ExternalBankAccountFormSource
} from '@klear/bank-account/wire.client'
import {
  clearExternalBankAccountFormErrors,
  markExternalBankAccountFormDirtyPaths,
  setExternalBankAccountFormField,
  type ExternalBankAccountFormFieldPath,
  type ExternalBankAccountFormFieldValue
} from '@/app/external-bank-account/shared/external-bank-account-form-state'
import {
  AccountFields,
  AddressFields,
  HolderFields
} from '@/app/external-bank-account/shared/external-bank-account-fields'

export type ExternalBankAccountStepGroupResult = { accountId: number }

export type ExternalBankAccountFlowMode = 'create' | 'edit' | 'view'

export type ExternalBankAccountAddressSeed = Partial<AddressJson> | null

export type ExternalBankAccountCommitArgs = {
  mode: 'create' | 'edit'
  formValues: ExternalBankAccountForm
  dirtyFields: ExternalBankAccountFormDirty
  accountId?: number
}

const STEP = {
  ACCOUNT: 'bank-account-account',
  HOLDER: 'bank-account-holder',
  ADDRESS: 'bank-account-address',
  REVIEW: 'bank-account-review'
} as const

type StepId = (typeof STEP)[keyof typeof STEP]

function hasErrors(errors: EntityFormErrorMap): boolean {
  return values(errors).some(Boolean)
}

function validateStep(stepId: StepId, vals: ExternalBankAccountForm): EntityFormErrorMap {
  const errs: EntityFormErrorMap = {}
  if (stepId === STEP.ACCOUNT) {
    if (!vals.nickname.trim()) errs.nickname = 'Nickname is required'
    if (!vals.accountHolderType) errs.accountHolderType = 'Select an account holder type'
    if (!vals.accountType) errs.accountType = 'Select an account type'
    if (!vals.accountNumber.trim()) errs.accountNumber = 'Account number is required'
    if (!vals.routingNumber.trim()) errs.routingNumber = 'Routing number is required'
    else if (!/^\d{9}$/.test(vals.routingNumber)) errs.routingNumber = 'Routing number must be 9 digits'
    if (vals.supportedPaymentNetworks.length === 0) errs.supportedPaymentNetworks = 'Select at least one network'
  } else if (stepId === STEP.HOLDER) {
    if (!vals.recipientName.trim()) errs.recipientName = 'Recipient name is required'
    if (!vals.recipientEmail.trim()) errs.recipientEmail = 'Recipient email is required'
    else if (!/.+@.+\..+/.test(vals.recipientEmail)) errs.recipientEmail = 'Enter a valid email'
    if (!vals.recipientPhone.trim()) errs.recipientPhone = 'Recipient phone is required'
  } else if (stepId === STEP.ADDRESS) {
    if (!vals.address.line1.trim()) errs['address.line1'] = 'Address line 1 is required'
    if (!vals.address.city.trim()) errs['address.city'] = 'City is required'
    if (!vals.address.state.trim()) errs['address.state'] = 'State is required'
    if (!vals.address.postal_code.trim()) errs['address.postal_code'] = 'Postal code is required'
    if (!vals.address.country.trim()) errs['address.country'] = 'Country is required'
  }
  return errs
}

function ReviewBody({ values: v }: { values: ExternalBankAccountForm }) {
  const networks = v.supportedPaymentNetworks
    .map((n) => (n === PAYMENT_NETWORK_ACH ? 'ACH' : n === PAYMENT_NETWORK_WIRE ? 'Wire' : n))
    .join(', ')
  const wireSupported = v.supportedPaymentNetworks.includes(PAYMENT_NETWORK_WIRE)
  const addressLine = wireSupported
    ? [v.address.line1, v.address.city, v.address.state, v.address.postal_code].filter(Boolean).join(', ') || '—'
    : '—'
  return (
    <Card>
      <CardHeader>
        <CardTitle>Review</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <Row label="Nickname" value={v.nickname || '—'} />
        <div className="grid grid-cols-2 gap-3">
          <Row label="Account number" value={v.accountNumber || '—'} />
          <Row label="Routing number" value={v.routingNumber || '—'} />
          <Row label="Holder type" value={v.accountHolderType || '—'} capitalize />
          <Row label="Account type" value={v.accountType || '—'} capitalize />
        </div>
        <Row label="Networks" value={networks || '—'} />
        <div className="border-t pt-3 grid grid-cols-2 gap-3">
          <Row label="Recipient name" value={v.recipientName || '—'} />
          <Row label="Recipient email" value={v.recipientEmail || '—'} />
          <Row label="Recipient phone" value={v.recipientPhone || '—'} />
          <Row label="Address" value={addressLine} />
        </div>
      </CardContent>
    </Card>
  )
}

function Row({ label, value, capitalize }: { label: string; value: string; capitalize?: boolean }) {
  return (
    <div>
      <span className="font-medium text-muted-foreground">{label}</span>
      <p className={capitalize ? 'capitalize' : undefined}>{value}</p>
    </div>
  )
}

const STEP_FIELDS: Record<StepId, readonly ExternalBankAccountFormFieldPath[]> = {
  [STEP.ACCOUNT]: [
    'nickname',
    'accountHolderType',
    'accountType',
    'accountNumber',
    'routingNumber',
    'supportedPaymentNetworks'
  ],
  [STEP.HOLDER]: ['recipientName', 'recipientEmail', 'recipientPhone'],
  [STEP.ADDRESS]: [
    'address.line1',
    'address.line2',
    'address.city',
    'address.state',
    'address.postal_code',
    'address.country'
  ],
  [STEP.REVIEW]: []
}

type ExternalBankAccountStepCtx = {
  vals: ExternalBankAccountForm
  errors: EntityFormErrorMap
  isEditing: boolean
  isAccountLocked: boolean
  wireSupported: boolean
  handleFieldChange: (path: ExternalBankAccountFormFieldPath, value: ExternalBankAccountFormFieldValue) => void
  validateBeforeNext: (stepId: StepId) => Promise<boolean>
  seedAddressIfNeeded: () => void
}

const accountStep: WizardStepModule<ExternalBankAccountStepCtx> = {
  id: STEP.ACCOUNT,
  label: 'Bank account',
  isComplete: (ctx) => !hasErrors(validateStep(STEP.ACCOUNT, ctx.vals)),
  beforeNext: (ctx) => ctx.validateBeforeNext(STEP.ACCOUNT),
  component: function BankAccountStep({ ctx }) {
    return (
      <AccountFields
        values={ctx.vals}
        errors={ctx.errors}
        onFieldChange={ctx.handleFieldChange}
        isEditing={ctx.isEditing}
        isAccountLocked={ctx.isAccountLocked}
      />
    )
  }
}

const holderStep: WizardStepModule<ExternalBankAccountStepCtx> = {
  id: STEP.HOLDER,
  label: 'Holder',
  isComplete: (ctx) => !hasErrors(validateStep(STEP.HOLDER, ctx.vals)),
  beforeNext: async (ctx) => {
    const ok = await ctx.validateBeforeNext(STEP.HOLDER)
    if (!ok) return false
    ctx.seedAddressIfNeeded()
    return true
  },
  component: function BankAccountHolderStep({ ctx }) {
    return (
      <HolderFields
        values={ctx.vals}
        errors={ctx.errors}
        onFieldChange={ctx.handleFieldChange}
        isEditing={ctx.isEditing}
      />
    )
  }
}

const addressStep: WizardStepModule<ExternalBankAccountStepCtx> = {
  id: STEP.ADDRESS,
  label: 'Address',
  visible: (ctx) => ctx.wireSupported,
  isComplete: (ctx) => !hasErrors(validateStep(STEP.ADDRESS, ctx.vals)),
  beforeNext: (ctx) => ctx.validateBeforeNext(STEP.ADDRESS),
  component: function BankAccountAddressStep({ ctx }) {
    return (
      <AddressFields
        values={ctx.vals}
        errors={ctx.errors}
        onFieldChange={ctx.handleFieldChange}
        isEditing={ctx.isEditing}
      />
    )
  }
}

const reviewStep: WizardStepModule<ExternalBankAccountStepCtx> = {
  id: STEP.REVIEW,
  label: 'Review',
  component: function BankAccountReviewStep({ ctx }) {
    return <ReviewBody values={ctx.vals} />
  }
}

const EXTERNAL_BANK_ACCOUNT_STEP_MODULES = [accountStep, holderStep, addressStep, reviewStep]

export type ExternalBankAccountFlowProps = {
  mode: ExternalBankAccountFlowMode
  accountId?: number
  account?: ExternalBankAccountFormSource | null
  addressSeed?: ExternalBankAccountAddressSeed
  isAccountLocked?: boolean
  cancelOnFirstStep?: boolean
  stepSelection?: 'free' | 'gated' | 'locked'
  isCommitting?: boolean
  onNavStateChange: (nav: WizardStepGroupNavState) => void
  onDirtyChange?: (dirty: boolean) => void
  onComplete: (result: ExternalBankAccountStepGroupResult) => void
  onCancel: () => void
  onCommit: (args: ExternalBankAccountCommitArgs) => Promise<{ id?: number } | null | undefined | void>
}

export function ExternalBankAccountFlow({
  mode,
  accountId,
  account,
  addressSeed,
  isAccountLocked = false,
  cancelOnFirstStep = false,
  stepSelection,
  isCommitting = false,
  onNavStateChange,
  onDirtyChange,
  onComplete,
  onCancel,
  onCommit
}: ExternalBankAccountFlowProps) {
  const isEditing = mode !== 'view'
  const resolvedStepSelection = stepSelection ?? (cancelOnFirstStep ? 'locked' : 'free')
  const defaultValues = useMemo(() => {
    if (mode === 'create' || account == null) return initialExternalBankAccountForm()
    const mapped = externalBankAccountReadToForm({
      accountDetails: account.accountDetails,
      billingDetails: account.billingDetails,
      supportedPaymentNetworks: account.supportedPaymentNetworks
    })
    return mapped.supportedPaymentNetworks.includes(PAYMENT_NETWORK_WIRE)
      ? seedExternalBankAccountFormAddress(mapped, addressSeed ?? null)
      : mapped
  }, [account, addressSeed, mode])

  const form = useEntityWizardForm<
    ExternalBankAccountForm,
    ExternalBankAccountFormDirty,
    ExternalBankAccountFormFieldPath,
    ExternalBankAccountFormFieldValue,
    StepId
  >({
    defaultValues,
    setField: setExternalBankAccountFormField,
    markDirtyPaths: markExternalBankAccountFormDirtyPaths,
    clearErrors: clearExternalBankAccountFormErrors,
    stepFields: (stepId) => STEP_FIELDS[stepId],
    validateStep,
    saveErrorFallback: 'Failed to save bank account'
  })
  const { values: vals, dirtyFields, errors, updateValues, validateBeforeNext } = form
  const [isSaving, setIsSaving] = useState(false)
  const isDirty = mode !== 'view' && values(dirtyFields).some((v) => v === true)
  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  const seedAddressIfNeeded = useCallback(() => {
    updateValues((draft: Draft<ExternalBankAccountForm>) => {
      if (!draft.supportedPaymentNetworks.includes(PAYMENT_NETWORK_WIRE)) return
      const seeded = seedExternalBankAccountFormAddress(draft as ExternalBankAccountForm, addressSeed ?? null)
      draft.address = seeded.address
    })
  }, [addressSeed, updateValues])

  useEffect(() => {
    if (addressSeed == null) return
    if (!vals.supportedPaymentNetworks.includes(PAYMENT_NETWORK_WIRE)) return
    if (vals.address.line1 || vals.address.city) return
    seedAddressIfNeeded()
  }, [addressSeed, seedAddressIfNeeded, vals.address.city, vals.address.line1, vals.supportedPaymentNetworks])

  const handleFieldChange = useCallback(
    (path: ExternalBankAccountFormFieldPath, value: ExternalBankAccountFormFieldValue) => {
      updateValues((draft: Draft<ExternalBankAccountForm>) => {
        setExternalBankAccountFormField(draft, path, value)
        if (path !== 'supportedPaymentNetworks') return
        const networks = value as ExternalBankAccountForm['supportedPaymentNetworks']
        if (!networks.includes(PAYMENT_NETWORK_WIRE)) return
        const seeded = seedExternalBankAccountFormAddress(draft as ExternalBankAccountForm, addressSeed ?? null)
        draft.address = seeded.address
      }, path)
    },
    [addressSeed, updateValues]
  )

  const wireSupported = vals.supportedPaymentNetworks.includes(PAYMENT_NETWORK_WIRE)
  const saving = isSaving || isCommitting

  const handleSubmit = useCallback(async () => {
    const allErrors: EntityFormErrorMap = {
      ...validateStep(STEP.ACCOUNT, vals),
      ...validateStep(STEP.HOLDER, vals),
      ...(wireSupported ? validateStep(STEP.ADDRESS, vals) : {})
    }
    if (hasErrors(allErrors)) {
      form.setErrors(allErrors)
      return
    }
    form.setErrors((current) => {
      const next = { ...current }
      delete next.root
      return next
    })
    setIsSaving(true)
    try {
      await form.runSubmit(async () => {
        if (mode === 'create') {
          const result = await onCommit({
            mode: 'create',
            formValues: vals,
            dirtyFields
          })
          if (result?.id == null) throw new Error('Creating the bank account did not return an id.')
          form.clearDirty()
          onComplete({ accountId: result.id })
        } else if (mode === 'edit') {
          if (accountId == null) throw new Error('Missing bank account context')
          await onCommit({
            mode: 'edit',
            formValues: vals,
            dirtyFields,
            accountId
          })
          form.clearDirty()
          onComplete({ accountId })
        }
      })
    } finally {
      setIsSaving(false)
    }
  }, [vals, wireSupported, form, mode, onCommit, dirtyFields, onComplete, accountId])

  const stepCtx = useMemo<ExternalBankAccountStepCtx>(
    () => ({
      vals,
      errors,
      isEditing,
      isAccountLocked,
      wireSupported,
      handleFieldChange,
      validateBeforeNext,
      seedAddressIfNeeded
    }),
    [
      vals,
      errors,
      isEditing,
      isAccountLocked,
      wireSupported,
      handleFieldChange,
      validateBeforeNext,
      seedAddressIfNeeded
    ]
  )
  const stepDefs = useMemo(() => bindWizardSteps(EXTERNAL_BANK_ACCOUNT_STEP_MODULES, stepCtx), [stepCtx])

  const flow = useReportedWizardFlow(stepDefs, {
    initialStepId: mode === 'view' ? STEP.REVIEW : STEP.ACCOUNT,
    onExitFirstStep: cancelOnFirstStep ? onCancel : undefined,
    stepSelection: resolvedStepSelection,
    onNavStateChange
  })

  const errorSummaryEntries = toPairs(errors).filter(
    ([key, value]) => key !== 'root' && isString(value) && value.length > 0
  )

  let submitLabel: string
  if (saving) {
    submitLabel = mode === 'create' ? 'Creating…' : 'Saving…'
  } else {
    submitLabel = mode === 'create' ? 'Create bank account' : 'Save changes'
  }

  return (
    <WizardFlowContent
      flow={flow}
      showNav={isEditing}
      className="w-full max-w-2xl space-y-4"
      onSubmit={() => void handleSubmit()}
      cancelOnFirstStep={cancelOnFirstStep}
      disabled={saving}
      nextIcon={mode === 'create' ? <Plus className="h-4 w-4 mr-1" /> : undefined}
      submitIcon={<Check className="h-4 w-4 mr-1" />}
      submitLabel={submitLabel}
      navClassName="flex justify-between gap-2"
    >
      {errorSummaryEntries.length > 0 && isEditing ? (
        <Alert variant="destructive">
          <AlertTitle>Fix the following before continuing</AlertTitle>
          <AlertDescription>
            <ul className="list-disc pl-5">
              {errorSummaryEntries.map(([key, value]) => (
                <li key={key}>{value}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      ) : null}
      {errors.root ? (
        <Alert variant="destructive">
          <AlertTitle>Could not save</AlertTitle>
          <AlertDescription>{errors.root}</AlertDescription>
        </Alert>
      ) : null}
      {form.saveError ? (
        <Alert variant="destructive">
          <AlertTitle>Could not save</AlertTitle>
          <AlertDescription>{form.saveError}</AlertDescription>
        </Alert>
      ) : null}
    </WizardFlowContent>
  )
}
